<!--
    Native Notes cell for the ADR 0014 grid (hosted renderer): the inline symbol-note editor,
    ported from the ag-hosted NotesCell. The symbol comes from the row through the resolved field
    id and the notes map from the service signal; edits write back through the service.
-->
<script lang="ts">
    import type { GridHostedCellProps } from '@thinkalpha/ui-table-svelte/svelteCellHost.svelte';
    import type { SymbolNotesService } from 'src/services/SymbolNotesService';

    interface Props extends GridHostedCellProps {
        /** Static per-column props: optional to satisfy the host contract, always provided. */
        symbolFieldId?: string;
        notesService?: SymbolNotesService;
    }

    let { cell, symbolFieldId, notesService: notesServiceProp }: Props = $props();
    // The service is a static per-column prop fixed at mount, so initial reads are deliberate.
    // svelte-ignore state_referenced_locally
    if (notesServiceProp === undefined) {
        throw new Error('NotesGridCell requires the notesService static prop');
    }
    // svelte-ignore state_referenced_locally
    const notesService = notesServiceProp;
    // Preact signals satisfy the svelte store contract, so $notes subscribes reactively.
    const { notes } = notesService;

    const symbol = $derived.by(() => {
        if (symbolFieldId === undefined) {
            return undefined;
        }
        const value = cell.row[symbolFieldId];
        return typeof value === 'string' ? value : undefined;
    });

    let isEditing = $state(false);
    let editValue = $state('');
    let inputRef = $state<HTMLTextAreaElement | undefined>(undefined);

    const currentNote = $derived(symbol !== undefined ? ($notes.get(symbol) ?? '') : '');

    // Truncate note for display (show first 50 chars)
    const displayNote = $derived(currentNote.length > 50 ? currentNote.slice(0, 50) + '...' : currentNote || '');

    function handleClick() {
        if (symbol === undefined) return;
        editValue = currentNote;
        isEditing = true;
        // Focus the input after the next tick
        setTimeout(() => inputRef?.focus(), 0);
    }

    function handleBlur() {
        if (symbol === undefined) return;
        isEditing = false;
        if (editValue !== currentNote) {
            notesService.updateNote(symbol, editValue);
        }
    }

    function handleKeyDown(e: KeyboardEvent) {
        // Stop propagation so the grid's own keyboard handling (Escape clears row selection)
        // never fires while a note is being edited.
        e.stopPropagation();

        if (e.key === 'Escape') {
            isEditing = false;
            editValue = currentNote;
        } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleBlur();
        }
    }
</script>

<div
    class="flex h-full w-full items-center"
    style="margin-inline: calc(-1 * var(--tka-grid-cell-padding, 11px)); width: calc(100% + 2 * var(--tka-grid-cell-padding, 11px));"
>
    {#if isEditing}
        <textarea
            bind:this={inputRef}
            bind:value={editValue}
            onblur={handleBlur}
            onkeydown={handleKeyDown}
            class="h-full w-full resize-none border-0 bg-gray-700/50 px-2 text-xs text-white focus:outline-none"
            placeholder="Add a note..."
        ></textarea>
    {:else}
        <button
            type="button"
            onclick={handleClick}
            class="hover:text-jade-400 h-full w-full cursor-pointer truncate px-2 text-left text-xs transition-colors {currentNote
                ? 'text-gray-300'
                : 'text-gray-500 italic'}"
        >
            {displayNote || 'Add note...'}
        </button>
    {/if}
</div>
